import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, ArrowRight, Target, Clock, Rocket, Trash2, RotateCcw, ShieldAlert, Archive } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Loader, EmptyState } from '@/components/common/Feedback';
import { CreateProjectDialog } from '@/components/projects/CreateProjectDialog';
import { OnboardingWizard } from '@/components/projects/OnboardingWizard';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { fmtMoneyShort, fmtDate } from '@/lib/format';
import { toast } from 'sonner';

export default function Projects() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Any signed-in member of the company can delete / restore their own projects.
  const canManage = !!user;

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizard, setWizard] = useState(false);

  // Soft-delete state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Restore state
  const [showDeleted, setShowDeleted] = useState(false);
  const [deletedProjects, setDeletedProjects] = useState([]);
  const [loadingDeleted, setLoadingDeleted] = useState(false);
  const [restoringId, setRestoringId] = useState(null);

  const load = () => api.get('/projects').then((r) => setProjects(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const loadDeleted = () => {
    setLoadingDeleted(true);
    api.get('/projects/deleted').then((r) => setDeletedProjects(r.data))
      .catch(() => toast.error('Could not load deleted projects'))
      .finally(() => setLoadingDeleted(false));
  };

  const openDeleted = () => { setShowDeleted(true); loadDeleted(); };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/projects/${deleteTarget.id}`);
      toast.success(`"${deleteTarget.name}" deleted — you can restore it from "Deleted projects"`);
      setDeleteTarget(null);
      setConfirmText('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete the project');
    } finally { setDeleting(false); }
  };

  const restore = async (p) => {
    setRestoringId(p.id);
    try {
      await api.post(`/projects/${p.id}/restore`);
      toast.success(`"${p.name}" restored`);
      loadDeleted();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not restore the project');
    } finally { setRestoringId(null); }
  };

  if (loading) return <Loader label="Loading projects..." />;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-slate-500">Your projects and their notice settings.</p>
        <div className="flex items-center gap-2">
          {canManage && (
            <Button variant="outline" onClick={openDeleted} data-testid="view-deleted-projects" className="text-slate-600">
              <Archive className="h-4 w-4 mr-1.5" />Deleted
            </Button>
          )}
          {projects.length > 0 && <CreateProjectDialog onCreated={load} />}
        </div>
      </div>

      {projects.length === 0 ? (
        <Card className="p-0 overflow-hidden">
          <div className="bg-ink-900 text-white px-8 py-10 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-brand-600/20 to-transparent pointer-events-none" />
            <div className="relative flex flex-col items-center">
              <div className="rounded-2xl bg-white/10 ring-1 ring-white/15 p-3 mb-4"><Rocket className="h-7 w-7 text-money" /></div>
              <h3 className="text-xl font-semibold font-display">Let&rsquo;s set up your first project</h3>
              <p className="text-sm text-slate-300 mt-1.5 max-w-md">It takes about a minute: name your project, paste your contract scope, and add a communication. Recoup does the rest.</p>
              <Button size="lg" className="mt-5 bg-money hover:bg-money-700 shadow-lifted" onClick={() => setWizard(true)} data-testid="start-onboarding">
                Get started <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            </div>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5">
          {projects.map((p) => {
            const m = p.metrics || {};
            return (
              <Card key={p.id} className="p-0 overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/projects/${p.id}`)} data-testid={`project-card-${p.id}`}>
                <div className="bg-ink-900 text-white px-5 py-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold truncate">{p.name}</div>
                      <div className="text-xs text-slate-400">{[p.client, p.location].filter(Boolean).join(' \u00b7 ') || 'No client set'}</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {canManage && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setDeleteTarget(p); setConfirmText(''); }}
                          className="p-1.5 rounded-md text-slate-400 hover:text-danger-100 hover:bg-white/10 transition-colors"
                          data-testid={`delete-project-${p.id}`} title="Delete project" aria-label="Delete project">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <ArrowRight className="h-4 w-4 text-slate-400" />
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-1.5 text-xs text-slate-300">
                    <Clock className="h-3.5 w-3.5 text-money" />
                    Notice period: {p.notice_period_days || 14} days <span className="text-slate-400">(project setting)</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 divide-x divide-slate-100">
                  <div className="px-4 py-3.5">
                    <div className="text-[10px] uppercase tracking-wide text-slate-400 flex items-center gap-1"><Target className="h-3 w-3" />Open</div>
                    <div className="text-lg font-semibold text-brand-600 mt-0.5" data-testid={`proj-open-${p.id}`}>{m.open_detections || 0}</div>
                    <div className="text-[11px] text-slate-400">opportunities</div>
                  </div>
                  <div className="px-4 py-3.5">
                    <div className="text-[10px] uppercase tracking-wide text-slate-400">Contract value</div>
                    <div className="text-lg font-semibold font-mono text-slate-800 mt-0.5">{p.contract_value ? fmtMoneyShort(p.contract_value) : '—'}</div>
                    <div className="text-[11px] text-slate-400">{p.contract_type || 'Contract'}</div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <OnboardingWizard open={wizard} onOpenChange={setWizard} onComplete={load} />

      {/* Delete confirmation (typed) */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) { setDeleteTarget(null); setConfirmText(''); } }}>
        <DialogContent className="max-w-md" data-testid="delete-project-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-danger-700"><ShieldAlert className="h-4 w-4" />Delete project</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-slate-600">
            <p>
              This will soft-delete <b className="text-slate-900">{deleteTarget?.name}</b> and hide it (and its opportunities,
              communications and change orders) from dashboards and search. You can restore it later from "Deleted projects".
            </p>
            <div>
              <label className="text-xs text-slate-500">Type <b className="font-mono text-slate-700">DELETE</b> to confirm</label>
              <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="DELETE"
                className="mt-1" data-testid="delete-confirm-input" autoFocus />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setDeleteTarget(null); setConfirmText(''); }} data-testid="delete-cancel-btn">Cancel</Button>
            <Button onClick={confirmDelete} disabled={confirmText !== 'DELETE' || deleting}
              className="bg-danger-600 hover:bg-danger-700 text-white" data-testid="delete-confirm-btn">
              {deleting ? 'Deleting…' : 'Delete project'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deleted projects (restore) */}
      <Dialog open={showDeleted} onOpenChange={setShowDeleted}>
        <DialogContent className="max-w-lg" data-testid="deleted-projects-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Archive className="h-4 w-4" />Deleted projects</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {loadingDeleted ? (
              <div className="py-8"><Loader label="Loading…" /></div>
            ) : deletedProjects.length === 0 ? (
              <EmptyState icon={Building2} title="No deleted projects" description="Deleted projects will appear here and can be restored." />
            ) : (
              <div className="space-y-2">
                {deletedProjects.map((p) => (
                  <div key={p.id} className="flex items-center justify-between rounded-lg border border-slate-200 p-3" data-testid={`deleted-project-${p.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-800 truncate">{p.name}</div>
                      <div className="text-[11px] text-slate-400">Deleted by {p.deleted_by || 'someone'} {p.deleted_at ? `· ${fmtDate(p.deleted_at)}` : ''}</div>
                    </div>
                    <Button size="sm" variant="outline" disabled={restoringId === p.id}
                      onClick={() => restore(p)} data-testid={`restore-project-${p.id}`} className="text-money-700 border-money/30 hover:bg-money-50">
                      <RotateCcw className="h-3.5 w-3.5 mr-1.5" />{restoringId === p.id ? 'Restoring…' : 'Restore'}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
